import { PipesModule } from '@lockton-apps/shared/ui/pipes';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { FieldTypeConfig, FormlyFieldConfig } from '@ngx-formly/core';
import { camelCase, get, replace, transform } from 'lodash';
import { debounceTime, filter, startWith, Subject, takeUntil } from 'rxjs';

import { DynamicFormsModule } from '@lockton-apps/dynamic-forms';
import {
  CurrencyConfig,
  ProposalQuickRaterQuote,
  ProposalQuickRaterQuotePayload,
} from '@lockton-apps/shared/interfaces';
import {
  AvatarModule,
  ButtonModule,
  LoadingOverlayModule,
} from '@lockton-apps/shared/ui/lui';
import { CobMeta, CobMetaService } from '@lockton-apps/shared/util/constants';
import { MaterialModule } from '@lockton-apps/material';

@Component({
  selector: 'la-proposal-rater',
  imports: [
    CommonModule,
    DynamicFormsModule,
    AvatarModule,
    MatIconModule,
    LoadingOverlayModule,
    ButtonModule,
    MaterialModule,
    PipesModule,
  ],
  templateUrl: './proposal-rater.component.html',
  styleUrls: ['./proposal-rater.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProposalRaterComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  @Input() form!: FormGroup;
  @Input() field!: FieldTypeConfig;
  @Input() portalId!: number;
  @Input() clientId!: number;
  @Input() productId!: string;
  @Input() sectionId!: string;
  @Input() productMeta!: Omit<CobMeta, 'id'>;
  @Input() quote!: ProposalQuickRaterQuote;
  @Input() currencyConfig!: CurrencyConfig;
  @Input() loading!: boolean;

  @Output() changed: EventEmitter<ProposalQuickRaterQuotePayload> =
    new EventEmitter();
  @Output() removed: EventEmitter<string> = new EventEmitter();

  readonly destroyer$: Subject<void> = new Subject();

  constructor(private cobMetaService: CobMetaService) {}

  ngAfterViewInit(): void {
    this.listenToFormChanges();

    this.form.setValidators(() =>
      this.quote && !this.quote.meta.valid ? { invalidQuote: true } : null
    );
  }

  ngOnChanges(): void {
    this.handleLoadingChanges();
  }

  ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  private listenToFormChanges(): void {
    this.form.valueChanges
      .pipe(
        startWith(this.form.value),
        debounceTime(600),
        filter(this.needToFetchNewQuote.bind(this)),
        takeUntil(this.destroyer$)
      )
      .subscribe((v) => this.changed.emit(this.buildPayload(v)));
  }

  private needToFetchNewQuote(model: unknown): boolean {
    // const cachedQuote: ProposalQuickRaterQuote | undefined = find(
    //   this.norwayRenewalFacade.quotes,
    //   {
    //     id: this.productId,
    //     meta: { payload: this.buildPayload(model) },
    //   }
    // );
    // const isFetching: boolean = this.form.disabled;

    // return isFormValid && !isFetching && !cachedQuote;

    // TODO: this functionality of checking against cache has not been migrated

    const isFormValid: boolean = this.form.valid;
    return isFormValid;
  }

  private handleLoadingChanges(): void {
    this.loading
      ? this.form.disable({ emitEvent: false })
      : this.form.enable({ emitEvent: false });
  }

  private buildPayload(formValue: unknown): ProposalQuickRaterQuotePayload {
    return {
      classOfBusinessId: this.cobMetaService.getClassOfBusinessById(
        this.productId
      ),
      portalId: this.portalId,
      clientId: this.clientId,
      formData: transform(
        this.field.props['fields'],
        (output: Record<string, unknown>, field: FormlyFieldConfig) => {
          const fieldId: string = replace(
            field.key as string,
            `${camelCase(this.productId)}.${this.sectionId}.`,
            ''
          );

          output[fieldId] = get(formValue, field.key as string);
        },
        {}
      ),
    };
  }
}
